import { CategoryTreeResponse, CategoryTree, CategoryNode, FlatCategory, CategoryTreeHistoryItem } from '../types/category';
import config from '../config/environment';

const API_URL = config.api.baseUrl;

// Common headers with authentication
const getHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
  };
};

const readError = async (response: Response, fallback: string) => {
  try {
    const data = await response.json();
    return data.error || data.message || fallback;
  } catch {
    return fallback;
  }
};

/**
 * Fetch the category tree for the authenticated tenant
 */
export const fetchCategoryTree = async (): Promise<CategoryTree> => {
  const response = await fetch(`${API_URL}/expenses/get-category-tree`, {
    method: 'GET',
    headers: getHeaders(),
    cache: 'no-store',
  });

  if (!response.ok) {
    throw new Error(await readError(response, `Failed to fetch category tree: ${response.statusText}`));
  }

  const data: CategoryTreeResponse = await response.json();
  return data.categoryTree;
};

export const saveCategoryTree = async (tree: CategoryTree): Promise<CategoryTree> => {
  const response = await fetch(`${API_URL}/expenses/put-category-tree`, {
    method: 'PUT',
    headers: getHeaders(),
    cache: 'no-store',
    body: JSON.stringify({ categoryTree: tree }),
  });

  if (!response.ok) {
    throw new Error(await readError(response, 'Failed to save category tree'));
  }

  const data: CategoryTreeResponse = await response.json();
  return data.categoryTree;
};

export const fetchCategoryTreeHistory = async (): Promise<CategoryTreeHistoryItem[]> => {
  const response = await fetch(`${API_URL}/expenses/category-tree-history`, {
    method: 'GET',
    headers: getHeaders(),
    cache: 'no-store',
  });

  if (!response.ok) {
    throw new Error(await readError(response, 'Failed to load category tree history'));
  }

  const data = await response.json();
  return Array.isArray(data.items) ? data.items : [];
};

export const restoreCategoryTree = async (snapshotId: string): Promise<CategoryTree> => {
  const response = await fetch(`${API_URL}/expenses/restore-category-tree`, {
    method: 'POST',
    headers: getHeaders(),
    cache: 'no-store',
    body: JSON.stringify({ id: snapshotId }),
  });

  if (!response.ok) {
    throw new Error(await readError(response, 'Failed to restore category tree'));
  }

  const data: CategoryTreeResponse = await response.json();
  return data.categoryTree;
};

/**
 * Flatten the category tree into a list of categories with their levels
 */
export const flattenCategoryTree = (tree: CategoryTree): {
  categories1: FlatCategory[];
  categories2: FlatCategory[];
  categories3: FlatCategory[];
  allCategories: FlatCategory[];
} => {
  const categories1: FlatCategory[] = [];
  const categories2: FlatCategory[] = [];
  const categories3: FlatCategory[] = [];
  const allCategories: FlatCategory[] = [];

  const processNode = (
    node: CategoryNode,
    level: 1 | 2 | 3,
    parentId?: string,
    parentPath: string[] = []
  ) => {
    const currentPath = [...parentPath, node.name];
    const flatCategory: FlatCategory = {
      id: node.id,
      name: node.name,
      level,
      parentId,
      path: currentPath,
    };

    allCategories.push(flatCategory);

    switch (level) {
      case 1:
        categories1.push(flatCategory);
        break;
      case 2:
        categories2.push(flatCategory);
        break;
      case 3:
        categories3.push(flatCategory);
        break;
    }

    // Process children
    if (node.children && level < 3) {
      node.children.forEach(child => {
        processNode(child, (level + 1) as 1 | 2 | 3, node.id, currentPath);
      });
    }
  };

  // Process all root categories
  tree.categories.forEach(category => {
    processNode(category, 1);
  });

  return { categories1, categories2, categories3, allCategories };
};

/**
 * Get subcategories for a given parent category ID
 */
export const getSubcategories = (
  allCategories: FlatCategory[],
  parentId: string,
  level: 2 | 3
): FlatCategory[] => {
  return allCategories.filter(cat => cat.parentId === parentId && cat.level === level);
};

/**
 * Find category by name (case-insensitive)
 */
export const findCategoryByName = (
  categories: FlatCategory[],
  name: string
): FlatCategory | undefined => {
  if (!name) return undefined;
  const normalizedName = name.trim().toLowerCase();
  return categories.find(
    cat => cat.name.toLowerCase() === normalizedName
  );
};

/**
 * Get the full category path for display
 */
export const getCategoryPath = (
  category1: string,
  category2?: string,
  category3?: string
): string => {
  const parts = [category1];
  if (category2) parts.push(category2);
  if (category3) parts.push(category3);
  return parts.join(' > ');
};